import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon, CopyIcon, Loader2, Trash2Icon } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { User } from "~/api/model";
import {
  getGetUserGuestAccessesQueryKey,
  useGetUserGuestAccesses,
  useRevokeGuestAccess,
} from "~/api/users/users";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { useCopyContent } from "~/hooks/useCopyContent";

interface GuestSecureLinksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  user: User;
}

export const GuestSecureLinksDialog: FC<GuestSecureLinksDialogProps> = ({
  open,
  onOpenChange,
  organizationId,
  user,
}) => {
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { handleCopy } = useCopyContent();

  const { data: guestAccesses, isLoading } = useGetUserGuestAccesses(
    organizationId,
    user.id,
    {
      query: {
        enabled: open,
      },
    }
  );

  const { mutate: revokeAccess, isPending: isRevoking } = useRevokeGuestAccess({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUserGuestAccessesQueryKey(organizationId, user.id),
        });
      },
      onError: () => {
        console.error("Failed to revoke guest access");
      },
    },
  });

  const handleClose = () => {
    setCopiedId(null);
    onOpenChange(false);
  };

  const handleCopyLink = (
    accessId: string | undefined,
    shareUrl: string | undefined
  ) => {
    if (accessId && shareUrl) {
      handleCopy(shareUrl);
      setCopiedId(accessId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleRevoke = (
    accessId: string | undefined,
    folderId: string | undefined
  ) => {
    if (accessId && folderId) {
      revokeAccess({
        organizationId,
        projectId: folderId,
        caseId: folderId,
        accessId,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg px-0">
        <DialogHeader className="px-6">
          <DialogTitle>
            <FormattedMessage
              id="settings.team.secureLinks.title"
              defaultMessage="Secure Links"
            />
          </DialogTitle>
          <DialogDescription>
            <FormattedMessage
              id="settings.team.secureLinks.description"
              defaultMessage="Manage secure case access links for {email}"
              values={{
                email: <strong className="font-bold">{user.email}</strong>,
              }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div className="flex justify-center items-center py-8 px-6">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
          ) : guestAccesses && guestAccesses.length > 0 ? (
            <div className="flex flex-col gap-2">
              <Label className="text-sm font-medium text-zinc-700 px-6">
                <FormattedMessage
                  id="settings.team.secureLinks.caseAccess"
                  defaultMessage="Case Access"
                />
              </Label>
              <div className="max-h-[412px] overflow-y-auto px-6">
                {guestAccesses.map((access) => (
                  <div
                    key={access.id}
                    className="flex items-center justify-between px-0 pb-[12px]"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="mb-[4px] truncate text-[#18181B] text-sm font-medium">
                        {access.folderName}
                      </span>
                      <span className="text-sm text-zinc-500 truncate">
                        {access.shareUrl}
                      </span>
                    </div>
                    <div className="flex items-center ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-500"
                        onClick={() =>
                          handleCopyLink(access.id, access.shareUrl)
                        }
                      >
                        {copiedId === access.id ? (
                          <CheckIcon className="h-4 w-4" />
                        ) : (
                          <CopyIcon className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-zinc-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleRevoke(access.id, access.folderId)}
                        disabled={isRevoking}
                      >
                        <Trash2Icon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8 px-6">
              <FormattedMessage
                id="settings.team.secureLinks.empty"
                defaultMessage="This guest has no active case access links"
              />
            </div>
          )}
          <div className="flex justify-end px-6 pt-2 border-t border-[#E4E4E7]">
            <Button variant="outline" onClick={handleClose}>
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
